import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import DragIndicatorOutlined from '@mui/icons-material/DragIndicatorOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import OverflowText from './OverflowText';
import StatusPill from './StatusPill';
import EmptyState from './EmptyState';

const LOADING_DELAY_MS = 200;

// Row controls (number, drag handle, remove X) show only on hover and focus.
const HOVER_CLASS = 'ft-transfer-hover';

const HIGHLIGHT_MS = 1500;

export const insertInCatalogOrder = (selectedIds, newIds, catalogOrder) => {
  const result = [...selectedIds];
  const orderOf = (id) =>
    catalogOrder.has(id) ? catalogOrder.get(id) : Number.POSITIVE_INFINITY;
  [...newIds]
    .sort((a, b) => orderOf(a) - orderOf(b))
    .forEach((id) => {
      const position = orderOf(id);
      let insertAt = 0;
      let bestBefore = -1;
      result.forEach((existing, index) => {
        const existingPosition = orderOf(existing);
        if (existingPosition < position && existingPosition > bestBefore) {
          bestBefore = existingPosition;
          insertAt = index + 1;
        }
      });
      result.splice(insertAt, 0, id);
    });
  return result;
};

/** true once `active` has stayed true for `delay` ms (avoids skeleton flashes). */
const useDelayedFlag = (active, delay) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!active) {
      setShown(false);
      return undefined;
    }
    const timer = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(timer);
  }, [active, delay]);
  return shown;
};

const smallCheckboxSx = {
  p: 0,
  flexShrink: 0,
  '& .MuiSvgIcon-root': { fontSize: 20 },
};

// ---------------------------------------------------------------------------
// List frame (header + scrolling body)
// ---------------------------------------------------------------------------

const ListFrame = ({ title, caption, height, children, busy }) => {
  const { ft } = useTheme().palette;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        border: `1px solid ${ft.neutrals.divider}`,
        borderRadius: `${ft.radius.control}px`,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '2px',
          p: 1.5,
          minHeight: 63,
          boxSizing: 'border-box',
          borderBottom: `1px solid ${ft.neutrals.borderLight}`,
        }}
      >
        <Typography
          sx={{ fontSize: 12, fontWeight: 500, color: ft.neutrals.textBody }}
        >
          {title}
        </Typography>
        {caption && (
          <Typography sx={{ fontSize: 12, color: ft.neutrals.textSecondary }}>
            {caption}
          </Typography>
        )}
      </Box>
      <Box
        aria-busy={busy || undefined}
        sx={{
          height,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Available list
// ---------------------------------------------------------------------------

const AvailableItem = ({ item, checked, onToggle }) => {
  const { ft } = useTheme().palette;
  const disabled = !!item.disabled;
  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: 30,
        minWidth: 0,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <Checkbox
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle(item.id)}
        inputProps={{ 'aria-label': item.label }}
        sx={smallCheckboxSx}
      />
      <OverflowText
        sx={{
          fontSize: 14,
          color: disabled ? ft.neutrals.textSecondary : ft.neutrals.textPrimary,
        }}
      >
        {item.label}
      </OverflowText>
      {disabled && item.disabledReason && (
        <Typography
          component="span"
          sx={{ flexShrink: 0, fontSize: 12, color: ft.neutrals.textSecondary }}
        >
          · {item.disabledReason}
        </Typography>
      )}
    </Box>
  );
};

const AvailableGroup = ({
  group,
  selectedSet,
  excludedSet,
  expanded,
  onToggleExpanded,
  onToggleItem,
  onToggleGroup,
}) => {
  const { ft } = useTheme().palette;
  const enabledItems = group.items.filter((i) => !i.disabled);
  const enabledSelected = enabledItems.filter((i) =>
    selectedSet.has(i.id)
  ).length;
  const counted = group.items.filter(
    (i) => selectedSet.has(i.id) && !excludedSet.has(i.id)
  ).length;
  const allSelected =
    enabledItems.length > 0 && enabledSelected === enabledItems.length;
  const someSelected = enabledSelected > 0 && !allSelected;
  const ExpandIcon = expanded ? ExpandMoreOutlined : ChevronRightOutlined;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderBottom: `1px solid ${ft.neutrals.borderLight}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          pl: 0.5,
          pr: 1.5,
          minHeight: 40,
        }}
      >
        <IconButton
          size="small"
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.label}`}
          onClick={() => onToggleExpanded(group.id)}
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            color: ft.neutrals.textSecondary,
            borderRadius: `${ft.radius.control}px`,
          }}
        >
          <ExpandIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box
          component="label"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexGrow: 1,
            minWidth: 0,
            cursor: enabledItems.length ? 'pointer' : 'default',
          }}
        >
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            disabled={enabledItems.length === 0}
            onChange={() => onToggleGroup(group, allSelected)}
            inputProps={{ 'aria-label': `Select all ${group.label}` }}
            sx={smallCheckboxSx}
          />
          <OverflowText
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: ft.neutrals.textPrimary,
            }}
          >
            {group.label}
          </OverflowText>
        </Box>
        <Typography
          component="span"
          sx={{ flexShrink: 0, fontSize: 12, color: ft.neutrals.textSecondary }}
        >
          {counted} of {group.items.length}
        </Typography>
      </Box>
      {expanded && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            pl: '38px',
            pr: 1.5,
            pb: 1,
          }}
        >
          {group.note && (
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.4,
                color: ft.neutrals.textSecondary,
                pb: '6px',
              }}
            >
              {group.note}
            </Typography>
          )}
          {group.items.map((item) => (
            <AvailableItem
              key={item.id}
              item={item}
              checked={selectedSet.has(item.id) && !excludedSet.has(item.id)}
              onToggle={onToggleItem}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Selected list
// ---------------------------------------------------------------------------

const SelectedRow = ({
  item,
  number,
  excluded,
  sortable,
  onRemove,
  onMoveKey,
  registerHandle,
  registerRow,
  isDragActive,
  highlighted,
}) => {
  const { ft } = useTheme().palette;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !sortable });

  let bgcolor = 'background.paper';
  if (isDragging) bgcolor = ft.neutrals.tile;
  else if (highlighted) bgcolor = ft.primary.tint;

  return (
    <Box
      ref={(node) => {
        setNodeRef(node);
        registerRow(item.id, node);
      }}
      data-testid={`selected-row-${item.id}`}
      data-highlighted={highlighted || undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        minHeight: 36,
        pl: '2px',
        pr: 0.5,
        borderBottom: `1px solid ${ft.neutrals.borderLight}`,
        bgcolor,
        position: 'relative',
        zIndex: isDragging ? 1 : 'auto',
        transform: CSS.Transform.toString(transform),
        // dnd-kit's transform transition plus a fade for the "just added" tint
        transition: [transition, 'background-color 600ms ease']
          .filter(Boolean)
          .join(', '),
        [`& .${HOVER_CLASS}`]: { opacity: isDragging ? 1 : 0 },
        [`&:hover .${HOVER_CLASS}, &:focus-within .${HOVER_CLASS}`]: {
          opacity: 1,
        },
        '@media (hover: none)': { [`& .${HOVER_CLASS}`]: { opacity: 1 } },
      }}
    >
      {sortable ? (
        <Box
          component="button"
          type="button"
          ref={(node) => {
            setActivatorNodeRef(node);
            registerHandle(item.id, node);
          }}
          className={HOVER_CLASS}
          aria-label={`Reorder ${item.label}. Drag, or use the up and down arrow keys.`}
          title="Drag to reorder"
          {...attributes}
          {...listeners}
          onKeyDown={(event) => {
            // Direct ArrowUp/ArrowDown moves; Space/Enter still starts a dnd-kit keyboard drag.
            if (
              !isDragActive &&
              (event.key === 'ArrowUp' || event.key === 'ArrowDown')
            ) {
              event.preventDefault();
              onMoveKey(item.id, event.key === 'ArrowUp' ? -1 : 1);
              return;
            }
            listeners?.onKeyDown?.(event);
          }}
          sx={{
            width: 26,
            height: 32,
            flexShrink: 0,
            p: 0,
            border: 0,
            bgcolor: 'transparent',
            color: ft.neutrals.textSecondary,
            cursor: isDragging ? 'grabbing' : 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: `${ft.radius.control}px`,
            touchAction: 'none',
            '&:focus-visible': {
              outline: `2px solid ${ft.primary.main}`,
              outlineOffset: -2,
            },
          }}
        >
          <DragIndicatorOutlined sx={{ fontSize: 18 }} />
        </Box>
      ) : (
        <Box sx={{ width: 26, flexShrink: 0 }} />
      )}
      <Typography
        component="span"
        className={HOVER_CLASS}
        aria-hidden="true"
        sx={{
          width: 20,
          flexShrink: 0,
          fontSize: 12,
          color: ft.neutrals.textSecondary,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {excluded ? '' : number}
      </Typography>
      <OverflowText
        sx={{
          flexGrow: 1,
          fontSize: 14,
          color: excluded ? ft.neutrals.textSecondary : ft.neutrals.textPrimary,
          textDecoration: excluded ? 'line-through' : 'none',
        }}
      >
        {item.label}
      </OverflowText>
      {excluded && (
        <StatusPill tone="neutral" label="Left out" sx={{ flexShrink: 0 }} />
      )}
      <IconButton
        size="small"
        className={HOVER_CLASS}
        aria-label={`Remove ${item.label}`}
        title="Remove"
        onClick={() => onRemove(item.id)}
        sx={{
          width: 30,
          height: 30,
          flexShrink: 0,
          color: ft.neutrals.textSecondary,
          borderRadius: `${ft.radius.control}px`,
        }}
      >
        <CloseOutlined sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Loading skeletons
// ---------------------------------------------------------------------------

const SKELETON_AVAILABLE = [
  { indent: false, width: '58%' },
  { indent: true, width: '44%' },
  { indent: true, width: '64%' },
  { indent: true, width: '50%' },
  { indent: false, width: '40%' },
  { indent: true, width: '58%' },
  { indent: true, width: '44%' },
  { indent: false, width: '58%' },
];
const SKELETON_SELECTED = [
  '58%',
  '72%',
  '40%',
  '64%',
  '30%',
  '50%',
  '68%',
  '44%',
];

const skeletonSx = (ft) => ({
  bgcolor: ft.neutrals.borderLight,
  borderRadius: `${ft.radius.control}px`,
  transform: 'none',
});

const AvailableSkeleton = () => {
  const { ft } = useTheme().palette;
  return (
    <Box
      data-testid="transfer-list-skeleton"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        p: 1.5,
        pt: 1.75,
      }}
    >
      {SKELETON_AVAILABLE.map((row, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: row.indent ? 2.75 : 0,
          }}
        >
          <Skeleton
            variant="rectangular"
            animation="wave"
            width={16}
            height={16}
            sx={{ ...skeletonSx(ft), flexShrink: 0 }}
          />
          <Skeleton
            variant="rectangular"
            animation="wave"
            height={12}
            width={row.width}
            sx={skeletonSx(ft)}
          />
        </Box>
      ))}
    </Box>
  );
};

const SelectedSkeleton = () => {
  const { ft } = useTheme().palette;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '17px',
        p: 1.5,
        pt: 1.75,
      }}
    >
      {SKELETON_SELECTED.map((width, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          animation="wave"
          height={12}
          width={width}
          sx={skeletonSx(ft)}
        />
      ))}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// TransferList
// ---------------------------------------------------------------------------

/**
 * TransferList — dual-list picker (UI Design Standards §4.10). Available items
 * in collapsible groups on the left; the ordered Selected list on the right,
 * reordered by drag and drop (@dnd-kit) or ArrowUp/ArrowDown on a row's handle.
 * Generic: no feature-specific names or logic.
 *
 * Props:
 *   groups          {Array<{ id, label, note?, items: Array<{ id, label, disabled?, disabledReason? }> }>}
 *   selectedIds     {Array}  ordered selected item ids
 *   onChange        {func}   (nextSelectedIds) => void
 *   sortable        {bool}   enables drag and keyboard reordering
 *   excludedIds     {Array}  selected ids shown struck through with a "Left out" pill;
 *                            not numbered and not counted in group counts
 *   defaultExpandedGroupIds {Array} groups open on first render (default: all)
 *   availableTitle / availableCaption / selectedTitle / selectedCaption {node}
 *   emptyText       {node}   Selected list empty state
 *   loading         {bool}   skeleton rows after ~200ms
 *   onReset / onClear {func} optional gray text buttons under the lists
 *   listHeight      {number} px height of each scrolling list (default 340)
 */
const TransferList = ({
  groups = [],
  selectedIds = [],
  onChange,
  sortable = false,
  excludedIds = [],
  defaultExpandedGroupIds,
  availableTitle = 'Available',
  availableCaption,
  selectedTitle,
  selectedCaption,
  emptyText = 'Nothing selected.',
  loading = false,
  onReset,
  onClear,
  listHeight = 340,
}) => {
  const { ft } = useTheme().palette;
  const showSkeleton = useDelayedFlag(loading, LOADING_DELAY_MS);

  const [expandedIds, setExpandedIds] = useState(
    () => new Set(defaultExpandedGroupIds || groups.map((g) => g.id))
  );
  // Groups arriving after the first render (catalog loaded) follow the default.
  const seenGroupIds = useRef(new Set(groups.map((g) => g.id)));
  useEffect(() => {
    const fresh = groups.filter((g) => !seenGroupIds.current.has(g.id));
    if (fresh.length === 0) return;
    fresh.forEach((g) => seenGroupIds.current.add(g.id));
    const open = fresh.filter(
      (g) => !defaultExpandedGroupIds || defaultExpandedGroupIds.includes(g.id)
    );
    if (open.length) {
      setExpandedIds((prev) => new Set([...prev, ...open.map((g) => g.id)]));
    }
  }, [groups, defaultExpandedGroupIds]);

  const itemsById = useMemo(() => {
    const map = new Map();
    groups.forEach((g) => g.items.forEach((i) => map.set(i.id, i)));
    return map;
  }, [groups]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const excludedSet = useMemo(() => new Set(excludedIds), [excludedIds]);
  // Ids not in the catalog are dropped from the Selected list.
  const selectedItems = selectedIds
    .map((id) => itemsById.get(id))
    .filter(Boolean);

  // Catalog position of every item, for placing newly checked items.
  const catalogOrder = useMemo(() => {
    const map = new Map();
    groups.forEach((g) => g.items.forEach((i) => map.set(i.id, map.size)));
    return map;
  }, [groups]);

  const emit = (next) => onChange && onChange(next);

  // Newly added rows: scrolled into view and briefly tinted.
  const [highlightIds, setHighlightIds] = useState([]);
  const pendingScrollId = useRef(null);
  const rowRefs = useRef(new Map());
  const registerRow = (id, node) => {
    if (node) rowRefs.current.set(id, node);
    else rowRefs.current.delete(id);
  };

  const add = (ids) => {
    if (ids.length === 0) return;
    pendingScrollId.current = ids[0];
    setHighlightIds(ids);
    emit(insertInCatalogOrder(selectedIds, ids, catalogOrder));
  };

  const toggleItem = (id) => {
    if (selectedSet.has(id)) emit(selectedIds.filter((x) => x !== id));
    else add([id]);
  };

  const toggleGroup = (group, allSelected) => {
    const enabledIds = group.items.filter((i) => !i.disabled).map((i) => i.id);
    if (allSelected) {
      const remove = new Set(enabledIds);
      emit(selectedIds.filter((x) => !remove.has(x)));
    } else {
      add(enabledIds.filter((x) => !selectedSet.has(x)));
    }
  };

  useEffect(() => {
    const id = pendingScrollId.current;
    if (!id) return;
    pendingScrollId.current = null;
    rowRefs.current.get(id)?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedIds]);

  useEffect(() => {
    if (highlightIds.length === 0) return undefined;
    const timer = setTimeout(() => setHighlightIds([]), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightIds]);

  const toggleExpanded = (groupId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  // ---- reordering -------------------------------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const [activeDragId, setActiveDragId] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const handleRefs = useRef(new Map());
  const pendingFocusId = useRef(null);

  const registerHandle = (id, node) => {
    if (node) handleRefs.current.set(id, node);
    else handleRefs.current.delete(id);
  };

  // Keep keyboard focus on the moved row's handle after a reorder.
  useEffect(() => {
    const id = pendingFocusId.current;
    if (!id) return;
    pendingFocusId.current = null;
    const node = handleRefs.current.get(id);
    if (node && document.activeElement !== node) node.focus();
  }, [selectedIds]);

  const visibleIds = selectedItems.map((i) => i.id);

  const moveTo = (id, toIndex) => {
    const from = visibleIds.indexOf(id);
    if (
      from < 0 ||
      toIndex < 0 ||
      toIndex >= visibleIds.length ||
      from === toIndex
    )
      return;
    const fromFull = selectedIds.indexOf(id);
    const toFull = selectedIds.indexOf(visibleIds[toIndex]);
    pendingFocusId.current = id;
    emit(arrayMove(selectedIds, fromFull, toFull));
    const item = itemsById.get(id);
    setAnnouncement(
      `${item?.label} moved to position ${toIndex + 1} of ${visibleIds.length}.`
    );
  };

  const handleMoveKey = (id, delta) =>
    moveTo(id, visibleIds.indexOf(id) + delta);

  const handleDragEnd = ({ active, over }) => {
    setActiveDragId(null);
    if (!over || active.id === over.id) return;
    moveTo(active.id, visibleIds.indexOf(over.id));
  };

  let number = 0;
  const buttonSx = {
    height: 32,
    px: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: 500,
    textTransform: 'none',
    color: ft.neutrals.textBody,
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 2,
        }}
      >
        <ListFrame
          title={availableTitle}
          caption={availableCaption}
          height={listHeight}
          busy={loading}
        >
          {loading
            ? showSkeleton && <AvailableSkeleton />
            : groups.map((group) => (
                <AvailableGroup
                  key={group.id}
                  group={group}
                  selectedSet={selectedSet}
                  excludedSet={excludedSet}
                  expanded={expandedIds.has(group.id)}
                  onToggleExpanded={toggleExpanded}
                  onToggleItem={toggleItem}
                  onToggleGroup={toggleGroup}
                />
              ))}
        </ListFrame>

        <ListFrame
          title={selectedTitle}
          caption={selectedCaption}
          height={listHeight}
          busy={loading}
        >
          {loading ? (
            showSkeleton && <SelectedSkeleton />
          ) : selectedItems.length === 0 ? (
            <Box sx={{ p: 1.5 }}>
              <EmptyState title={emptyText} sx={{ py: 4 }} />
            </Box>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={({ active }) => setActiveDragId(active.id)}
              onDragCancel={() => setActiveDragId(null)}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={visibleIds}
                strategy={verticalListSortingStrategy}
              >
                {selectedItems.map((item) => {
                  const excluded = excludedSet.has(item.id);
                  if (!excluded) number += 1;
                  return (
                    <SelectedRow
                      key={item.id}
                      item={item}
                      number={number}
                      excluded={excluded}
                      sortable={sortable}
                      onRemove={toggleItem}
                      onMoveKey={handleMoveKey}
                      registerHandle={registerHandle}
                      registerRow={registerRow}
                      isDragActive={activeDragId !== null}
                      highlighted={highlightIds.includes(item.id)}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          )}
        </ListFrame>
      </Box>

      {(onReset || onClear) && (
        <Box sx={{ display: 'flex', gap: 0.5, pl: 0.25 }}>
          {onReset && (
            <Button
              variant="text"
              onClick={onReset}
              disabled={loading}
              sx={buttonSx}
            >
              Reset to defaults
            </Button>
          )}
          {onClear && (
            <Button
              variant="text"
              onClick={onClear}
              disabled={loading}
              sx={buttonSx}
            >
              Clear all
            </Button>
          )}
        </Box>
      )}

      <Box
        aria-live="polite"
        sx={{
          // Visually hidden, still read by screen readers
          position: 'absolute',
          top: 0,
          left: 0,
          width: '1px',
          height: '1px',
          p: 0,
          m: '-1px',
          border: 0,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
        }}
      >
        {announcement}
      </Box>
    </Box>
  );
};

export default TransferList;
